import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../config/axios";
import {
  ReservaEstacionamiento,
  ReservaEstacionamientoCreate,
  ReservaEstacionamientoUpdate,
} from "../types/ReservaEstacionamiento";
import { logger } from "../utils/logger";
import {
  invalidateParkingConfigQueries,
  invalidateParkingReservationQueries,
} from "../utils/queryInvalidation";

interface UpdateGlobalPricesRequest {
  tipo: "global";
  porcentaje: number;
}

interface UpdateIndividualPriceRequest {
  tipo: "individual";
  estacionamiento: string;
  precio_base: number;
}

type UpdatePricesRequest =
  | UpdateGlobalPricesRequest
  | UpdateIndividualPriceRequest;

interface RevertPricesRequest {
  estacionamiento?: string;
}

// Función para crear una reserva de estacionamiento
const createEstacionamientoReservation = async (
  data: ReservaEstacionamientoCreate
): Promise<ReservaEstacionamiento> => {
  const response = await api.post<ReservaEstacionamiento>(
    "/reservas-estacionamiento",
    data
  );
  return response.data;
};

// Hook para crear reservas de estacionamiento
export const useCreateEstacionamientoReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEstacionamientoReservation,
    onSuccess: () => {
      invalidateParkingReservationQueries(queryClient);
    },
    onError: (error) => {
      logger.error("Error al crear reserva de estacionamiento:", error);
    },
  });
};

// Hook para actualizar una reserva de estacionamiento
const updateEstacionamientoReservation = async (
  data: ReservaEstacionamientoUpdate
): Promise<ReservaEstacionamiento> => {
  const { id, ...updateData } = data;
  try {
    const response = await api.put<ReservaEstacionamiento>(
      `/reservas-estacionamiento/${id}`,
      updateData
    );
    return response.data;
  } catch (error) {
    logger.error("Error al actualizar la reserva de estacionamiento:", error);
    throw error;
  }
};

export const useUpdateEstacionamientoReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEstacionamientoReservation,
    onSuccess: () => {
      invalidateParkingReservationQueries(queryClient);
    },
  });
};

export const useUpdateGlobalPrices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdatePricesRequest) => {
      const response = await api.put("/estacionamientos/precios", data);
      return response.data;
    },
    onSuccess: () => {
      invalidateParkingConfigQueries(queryClient);
    },
    onError: (error) => {
      logger.error("Error updating global prices:", error);
    },
  });
};

export const useUpdateIndividualPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateIndividualPriceRequest) => {
      const response = await api.put("/estacionamientos/precios", data);
      return response.data;
    },
    onSuccess: () => {
      invalidateParkingConfigQueries(queryClient);
    },
    onError: (error) => {
      logger.error("Error updating individual price:", error);
    },
  });
};

export const useRevertPrices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RevertPricesRequest = {}) => {
      const response = await api.post(
        "/estacionamientos/revertir-precios",
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateParkingConfigQueries(queryClient);
    },
    onError: (error) => {
      logger.error("Error reverting prices:", error);
    },
  });
};

// Hook para eliminar una reserva de estacionamiento
const deleteEstacionamientoReservation = async (
  id: number
): Promise<unknown> => {
  try {
    const response = await api.delete(`/reservas-estacionamiento/${id}`);
    return response.data;
  } catch (error) {
    logger.error("Error al eliminar la reserva de estacionamiento:", error);
    throw error;
  }
};

export const useDeleteEstacionamientoReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEstacionamientoReservation,
    onSuccess: () => {
      invalidateParkingReservationQueries(queryClient);
    },
  });
};
